import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/authContext";
import SEO from "@/components/seo/SEO";
import { Lock, LogIn, AlertCircle } from "lucide-react";

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@ycn.tv");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && user.role === "admin") nav("/admin", { replace: true });
  }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await login(email.trim().toLowerCase(), password);
    setSubmitting(false);
    if (!res.success) {
      setError(res.error || "Login failed");
      return;
    }
    if (res.user.role !== "admin") {
      setError("This account does not have admin access.");
      return;
    }
    nav("/admin", { replace: true });
  };

  if (loading) return <div className="pt-32 text-center text-white/50">Loading…</div>;
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  return (
    <div data-testid="admin-login-page" className="min-h-screen flex items-center justify-center px-6 py-24 relative overflow-hidden">
      <SEO title="Admin Sign In" description="Restricted access — YCN admin sign in." path="/admin/login" noindex />
      <div className="absolute inset-0 -z-10 bg-[#050505]" />
      <div className="absolute inset-0 -z-10 opacity-60" style={{ backgroundImage: "radial-gradient(50% 60% at 20% 30%, rgba(124,58,237,0.35), transparent 60%), radial-gradient(45% 55% at 85% 70%, rgba(249,115,22,0.28), transparent 60%)" }} />
      <div className="relative w-full max-w-md ycn-glass rounded-3xl p-8 md:p-10 border border-white/10">
        <div className="flex items-center gap-2 mb-6 text-[10px] uppercase tracking-[0.35em] text-white/60">
          <Lock size={12} /> Restricted · Admin Access
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-black leading-tight">
          <span className="ycn-gradient-text">YCN</span> Admin
        </h1>
        <p className="mt-1 text-white/60 text-sm">Sign in to manage the YCN catalog.</p>

        <form onSubmit={onSubmit} className="mt-7 space-y-3">
          <div>
            <label className="block text-[11px] uppercase tracking-[0.25em] text-white/50 mb-1.5">Email</label>
            <input
              type="email" required autoFocus value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full rounded-full bg-white/5 border border-white/15 focus:border-white/40 focus:bg-white/10 outline-none px-5 py-3 text-white placeholder:text-white/40 transition"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.25em] text-white/50 mb-1.5">Password</label>
            <input
              type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="w-full rounded-full bg-white/5 border border-white/15 focus:border-white/40 focus:bg-white/10 outline-none px-5 py-3 text-white placeholder:text-white/40 transition"
            />
          </div>
          {error && (
            <div data-testid="admin-login-error" className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 flex items-start gap-2 text-sm text-red-200">
              <AlertCircle size={16} className="shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}
          <button
            type="submit" disabled={submitting}
            data-testid="admin-login-submit"
            className="ycn-btn-primary w-full py-3 rounded-full font-bold text-white disabled:opacity-60 inline-flex items-center justify-center gap-2 transition-all"
          >
            {submitting ? "Signing in…" : (<><LogIn size={16} /> Sign In</>)}
          </button>
        </form>
      </div>
    </div>
  );
}
